import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme, Spinner } from 'tamagui'
import { CobuContainer, CobuStack, CobuText, CobuButton } from '@cobu/ui'
import { Strings } from '../../../constants/Strings'
import { useQuery } from '@tanstack/react-query'
import { getReferralUserSettings, updateReferralUserSettings } from '@cobu/api-client'
import { ArrowLeft } from '@tamagui/lucide-icons'
import { showErrorMessage, showSuccessMessage } from '../../../utils/messageToast'

export default function CustomizeReferralMessageScreen() {
  const navigate = useNavigate()
  const theme = useTheme()
  const strings = Strings.get().referral.customize

  const { data: userSettings, isLoading } = useQuery({
    queryKey: ['referralUserSettings'],
    queryFn: getReferralUserSettings,
  })

  const [customMessage, setCustomMessage] = React.useState('')
  const [isSaving, setIsSaving] = React.useState(false)

  React.useEffect(() => {
    if (userSettings?.welcome_message) setCustomMessage(userSettings.welcome_message)
  }, [userSettings?.welcome_message])

  const onSaveMessage = React.useCallback(async () => {
    try {
      setIsSaving(true)
      await updateReferralUserSettings({ welcome_message: customMessage })
      showSuccessMessage(strings.messages.welcomeMessageSaved)
      navigate(-1)
    } catch {
      showErrorMessage(strings.messages.welcomeMessageSaveFailed)
    } finally {
      setIsSaving(false)
    }
  }, [customMessage, navigate, strings.messages.welcomeMessageSaved, strings.messages.welcomeMessageSaveFailed])

  if (isLoading) {
    return (
      <CobuContainer f={1} bg="$white" jc="center" ai="center">
        <Spinner size="large" color={(theme.cobuTeal?.val as any) ?? '#14b8a6'} />
      </CobuContainer>
    )
  }

  return (
    <CobuContainer f={1} bg="$white">
      <CobuStack layout="horizontal" ai="center" px="$4" py="$2" bbw={1} bbc="$backgroundMuted" bg="$white">
        <CobuButton onPress={() => navigate(-1)} bw={0} bg="$white" p="$0">
          <ArrowLeft size={24} color={(theme.cobuDarkBlue?.val as any) ?? '#272163'} />
        </CobuButton>
        <CobuText variant="heading" fontSize="$5" fontWeight="bold" color="$cobuDarkBlue" f={1}>
          {Strings.get().referral.customize.welcomeMessage}
        </CobuText>
        <CobuButton onPress={onSaveMessage} disabled={isSaving} bg="$white" bw={0} p="$0">
          <CobuText variant="body" color="$textLinkColor" fontWeight="medium">
            {Strings.get().common.buttonLabels.save}
          </CobuText>
        </CobuButton>
      </CobuStack>

      <CobuStack px="$4" py="$3" gap="$3">
        <CobuText col="$cobuDarkBlue" fontSize="$1" fontWeight="bold">
          {Strings.get().referral.customize.welcomeMessage}
        </CobuText>
        <textarea
          placeholder={Strings.get().referral.customize.welcomeMessagePlaceholder}
          maxLength={500}
          value={customMessage}
          onChange={(e) => setCustomMessage(e.target.value)}
          style={{
            width: '100%',
            minHeight: 160,
            padding: 10,
            fontSize: 16,
            lineHeight: 2,
            fontFamily: 'inherit',
            borderRadius: 8,
            border: '1px solid var(--color-border, #e5e7eb)',
            resize: 'vertical',
          }}
        />
      </CobuStack>

      {isSaving && (
        <CobuStack pos="absolute" t={0} l={0} r={0} b={0} jc="center" ai="center" bg="$blackTransparent50">
          <Spinner size="large" color={(theme.white?.val as any) ?? '#ffffff'} />
        </CobuStack>
      )}
    </CobuContainer>
  )
}
